/**
 * Unified RPC Client Helper
 * 
 * This is the central RPC client that all domain-specific RPC clients should use.
 * It provides:
 * - Consistent URL building with company slug validation
 * - Consistent error handling
 * - Request/response interceptors
 * - Timeout configuration
 * 
 * Usage:
 * ```typescript
 * import { rpcClient } from './rpcClient';
 * 
 * const rpcDomain = {
 *   list() {
 *     return rpcClient.post('domain', '/list', {});
 *   }
 * };
 * ```
 */

import { apiClient } from '../apiClient';
import type { AxiosRequestConfig, AxiosResponse } from 'axios';

/**
 * Helper to extract company slug from current URL path
 * CRITICAL: Returns empty string if company not found to prevent malformed URLs
 */
export const getCompanySlug = (): string => {
  if (typeof window === 'undefined') return '';
  const parts = window.location.pathname.split('/').filter(Boolean);
  const firstPart = parts[0] || '';
  
  // CRITICAL FIX: Ignore superadmin routes - they don't have a company slug
  // Routes like /superadmin/company/... should not use company slug
  if (firstPart === 'superadmin' || firstPart === 'admin' || firstPart === 'manager' || firstPart === 'agent') {
    return '';
  }
  
  // CRITICAL FIX: Validate company slug to prevent empty/malformed URLs
  // Empty company would result in /api//rpc/... which causes 404
  if (!firstPart || firstPart === 'undefined' || firstPart.trim() === '') {
    console.warn('[rpcClient] Company slug is empty or invalid:', {
      pathname: window.location.pathname,
      parts,
    });
    return '';
  }
  
  return firstPart;
};

/**
 * Build RPC endpoint URL
 * CRITICAL: Validates company before building URL to prevent 404s
 * 
 * @param domain - The RPC domain (e.g., 'clients', 'leads', 'admin/tasks')
 * @param path - The endpoint path (e.g., '/list', '/create')
 * @param requireCompany - Whether company slug is required (default: true)
 * @returns The full RPC URL
 * @throws Error if company is required but not available
 */
export const buildRpcUrl = (
  domain: string,
  path: string,
  requireCompany: boolean = true
): string => {
  // Some routes don't require company (e.g., /api/rpc/superadmin, /api/rpc/contact)
  if (!requireCompany) {
    return `/api/rpc/${domain}${path}`;
  }
  
  const company = getCompanySlug();
  
  // CRITICAL FIX: Prevent API calls with empty company (would cause 404)
  if (!company) {
    const error = new Error(
      'Company slug is required but not available. Please ensure you are on a valid company page.'
    );
    console.error('[rpcClient] buildRpcUrl failed:', error.message, {
      pathname: window.location.pathname,
      domain,
      path,
    });
    throw error; // Throw error to prevent malformed request
  }
  
  return `/api/${company}/rpc/${domain}${path}`;
};

/**
 * Unified RPC Client
 * 
 * Provides consistent methods for making RPC calls with:
 * - Automatic company slug injection
 * - Consistent error handling
 * - Standardized request/response format
 */
export const rpcClient = {
  /**
   * Make a POST request to an RPC endpoint
   * 
   * @param domain - The RPC domain (e.g., 'clients', 'leads', 'admin/tasks')
   * @param path - The endpoint path (e.g., '/list', '/create')
   * @param payload - The request payload
   * @param options - Additional Axios options (timeout, headers, etc.)
   * @param requireCompany - Whether company slug is required (default: true)
   * @returns Promise resolving to AxiosResponse
   */
  async post<T = any>(
    domain: string,
    path: string,
    payload?: any,
    options?: AxiosRequestConfig & { requireCompany?: boolean },
    requireCompany?: boolean
  ): Promise<AxiosResponse<T>> {
    const companyRequired = requireCompany ?? options?.requireCompany ?? true;
    const url = buildRpcUrl(domain, path, companyRequired);
    
    // Remove requireCompany from options if present (not a valid Axios option)
    const { requireCompany: _, ...axiosOptions } = options || {};
    
    return apiClient.post<T>(url, payload, axiosOptions);
  },

  /**
   * Make a GET request to an RPC endpoint
   * 
   * @param domain - The RPC domain
   * @param path - The endpoint path
   * @param params - Query parameters
   * @param options - Additional Axios options
   * @param requireCompany - Whether company slug is required (default: true)
   * @returns Promise resolving to AxiosResponse
   */
  async get<T = any>(
    domain: string,
    path: string,
    params?: Record<string, any>,
    options?: AxiosRequestConfig & { requireCompany?: boolean },
    requireCompany?: boolean
  ): Promise<AxiosResponse<T>> {
    const companyRequired = requireCompany ?? options?.requireCompany ?? true;
    const url = buildRpcUrl(domain, path, companyRequired);
    
    const { requireCompany: _, ...axiosOptions } = options || {};
    
    return apiClient.get<T>(url, {
      ...axiosOptions,
      params,
    });
  },

  /**
   * Make a PUT request to an RPC endpoint
   * 
   * @param domain - The RPC domain
   * @param path - The endpoint path
   * @param payload - The request payload
   * @param options - Additional Axios options
   * @param requireCompany - Whether company slug is required (default: true)
   * @returns Promise resolving to AxiosResponse
   */
  async put<T = any>(
    domain: string,
    path: string,
    payload?: any,
    options?: AxiosRequestConfig & { requireCompany?: boolean },
    requireCompany?: boolean
  ): Promise<AxiosResponse<T>> {
    const companyRequired = requireCompany ?? options?.requireCompany ?? true;
    const url = buildRpcUrl(domain, path, companyRequired);
    
    const { requireCompany: _, ...axiosOptions } = options || {};
    
    return apiClient.put<T>(url, payload, axiosOptions);
  },

  /**
   * Make a DELETE request to an RPC endpoint
   * 
   * @param domain - The RPC domain
   * @param path - The endpoint path
   * @param options - Additional Axios options
   * @param requireCompany - Whether company slug is required (default: true)
   * @returns Promise resolving to AxiosResponse
   */
  async delete<T = any>(
    domain: string,
    path: string,
    options?: AxiosRequestConfig & { requireCompany?: boolean },
    requireCompany?: boolean
  ): Promise<AxiosResponse<T>> {
    const companyRequired = requireCompany ?? options?.requireCompany ?? true;
    const url = buildRpcUrl(domain, path, companyRequired);
    
    const { requireCompany: _, ...axiosOptions } = options || {};
    
    return apiClient.delete<T>(url, axiosOptions);
  },
};

export default rpcClient;

